import { format, isPast } from "date-fns";
import { Calendar, Clock, MapPin, Video, Users, CheckCircle, AlertCircle } from "lucide-react";
import Card from "./Card";
import type { Call } from "../types";

interface CallCardProps {
  call: Call;
  onClick: () => void;
}

export default function CallCard({ call, onClick }: CallCardProps) {
  const isPastCall = isPast(new Date(call.scheduledAt)) || call.status === "completed";
  const prepStatus = call.prepPhase?.status || "not_started";

  const getPrepStatusBadge = () => {
    if (isPastCall) {
      return call.transcription ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
          <CheckCircle className="w-3 h-3" />
          Completed
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
          Past
        </span>
      );
    }

    switch (prepStatus) {
      case "completed":
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
            <CheckCircle className="w-3 h-3" />
            Prepared
          </span>
        );
      case "in_progress":
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
            <Clock className="w-3 h-3" />
            Preparing
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
            <AlertCircle className="w-3 h-3" />
            Not Prepared
          </span>
        );
    }
  };

  return (
    <Card onClick={onClick} className="mb-3">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{call.clientName}</h3>
          {call.companyName && (
            <p className="text-sm text-gray-500">{call.companyName}</p>
          )}
        </div>
        {getPrepStatusBadge()}
      </div>

      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-gray-400" />
          {format(new Date(call.scheduledAt), "MMM d, yyyy")}
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-gray-400" />
          {format(new Date(call.scheduledAt), "h:mm a")}
        </div>
        {call.isVirtual ? (
          <div className="flex items-center gap-1.5">
            <Video className="w-4 h-4 text-gray-400" />
            Virtual
          </div>
        ) : call.location ? (
          <div className="flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-gray-400" />
            {call.location}
          </div>
        ) : null}
        {call.participants && call.participants.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-gray-400" />
            {call.participants.length} participant{call.participants.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>
    </Card>
  );
}
